/**
 * Shared message content processing logic used by both the main Ask Yogi
 * conversation (Container.tsx) and the Ask Yogi Anywhere widget (utils.ts).
 */

export interface ProcessedParts {
  parts: string[]
  charts: string[]
  chartArtifactIds: (string | null)[]
  annotationArtifactIds: (string | null)[]
  // Verbatim card slot per part boundary — a rendered VerbatimCard, or null
  // for a boundary that's a chart/annotation/unknown artifact. Aligned with
  // parts[] the same way chartArtifactIds[]/annotationArtifactIds[] are.
  verbatims: (VerbatimCitation | null)[]
  tables: Array<{ resultIds: string[]; title?: string }>
  // Raw markdown source for each tagged `<table>…</table>` block, aligned with
  // `tables[]` by index. Empty string for tables where inner content couldn't
  // be extracted (open/close tag mismatch). Consumed by TableToolbar to build
  // its clipboard payload for a single table.
  tableMarkdown: string[]
  // Distinct citation record ids referenced in this message (inline pills
  // and verbatim cards alike), in first-seen order. Consumed by the
  // message-level batch fetch (useCitationsData).
  citationIds: string[]
  // Distinct post_text ids named by a verbatim's citation, for resolving a
  // theme/sentiment alongside the batch citation fetch (useCitationsData).
  postTextIds: string[]
}

// A `<citation post_id="..." post_text_id="..." />` tag's parsed attributes.
export interface ParsedCitationTag {
  postId: string
  postTextId: string | null
}

// One markdown blockquote the agent quoted, with the record + (optionally)
// sentence it came from. `quote` is the snippet from the markdown itself —
// NOT the citation API's `body` — so it's available before the API call
// resolves.
export interface VerbatimCitation {
  postId: string
  postTextId: string | null
  quote: string
}

// Matches either the new self-closing attribute form (mirrors the already-
// proven <artifact type="..." id="..." /> marker shape in this same file)
// or the legacy pre-redesign bare-integer form `<citation>123</citation>`
// (old stored conversations only — never re-emitted going forward).
const CITATION_REGEX = /<citation\s+.*?\/>|<citation>[^<]*<\/citation>/g
// Web citations carry url/title/source attributes in any order and may wrap
// display text: <web-citation url=... title=... source=...>…</web-citation>.
const WEB_CITATION_REGEX = /<web-citation\b([^>]*)>[^<]*<\/web-citation>/g
const WEB_CITATION_ATTR = /\b(url|title|source)="([^"]*)"/g
// Matches an entire `<table results="…" title="…">…</table>` block so the
// inner markdown (needed by TableToolbar) is captured in the same pass that
// records the tables[] metadata.
const TABLE_BLOCK_REGEX =
  /<table results="([^"]+)"(?:\s+title="([^"]*)")?\s*>([\s\S]*?)<\/table>/g
const TABLE_OPENING_REGEX =
  /<table results="([^"]+)"(?:\s+title="([^"]*)")?\s*>/g
const TABLE_CLOSING_REGEX = /<\/table>/g
// Matches both legacy <chart ...>...</chart> and new <artifact type=... id=... />
// The artifact format supports any attribute order and optional quotes around values.
const ARTIFACT_ID_RE = /\bid=["']?([^"'\s/>]+)["']?/
const ARTIFACT_TYPE_RE = /\btype=["']?([^"'\s/>]+)["']?/
const COMBINED_ARTIFACT_REGEX =
  /(<artifact\s+.*?\/>|<chart\b[^>]*>[\s\S]*?<\/chart>)/g

// A consumer-verbatim block: an optional LLM-authored metadata list-item line
// (the backward-compat case for old conversations — see message-processing's
// docs) immediately followed by one or more consecutive blockquote lines.
//   group 1 — leading boundary ('' at string start, otherwise '\n'); re-emitted
//   group 2 — the blockquote body, WITHOUT its trailing newline (an adjacent
//             second verbatim block would otherwise never match — see below)
const VERBATIM_BLOCK_REGEX =
  /(^|\n)(?:[ \t]*(?:[-*+]|\d{1,3}[.)])[ \t]+[^\n]*\n)?([ \t]*>[^\n]*(?:\n[ \t]*>[^\n]*)*)/g
// A closed citation tag inside a candidate verbatim block. Reused (not the
// module-level CITATION_REGEX) so matching it never disturbs that regex's
// state, and so exactly-one-match / non-numeric-id bail cleanly here without
// touching inline-citation handling below.
const VERBATIM_CITATION_REGEX =
  /<citation\s+.*?\/>|<citation>[^<]*<\/citation>/g
// Leading/trailing double quotes only (straight or curly). Single quotes are
// deliberately excluded — a quote ending in an apostrophe (e.g. "lovin'")
// must not lose its final character.
const SURROUNDING_QUOTE_OPEN = /^["“„]\s*/
const SURROUNDING_QUOTE_CLOSE = /\s*["”]$/

// Extracts one attribute's value from a citation tag's full matched text.
// Independent per attribute, the same property `web-citation`'s own
// attribute regex already relies on (see WEB_CITATION_ATTR above) — a
// malformed or absent post_text_id can't prevent post_id from resolving.
function extractCitationAttr(tag: string, name: string): string | null {
  const match = new RegExp(`\\b${name}="(\\d+)"`).exec(tag)
  return match ? match[1]! : null
}

/**
 * Parse a full matched `<citation post_id="..." post_text_id="..." />` tag,
 * or the legacy pre-redesign `<citation>123</citation>` form (old stored
 * conversations only — never re-emitted going forward). Returns null when
 * no usable post_id is present (missing, non-numeric, or the legacy tag was
 * empty) so the caller falls back to leaving the tag/block untouched — never
 * a raw, unparsed string treated as an id.
 */
export function parseCitationTag(tag: string): ParsedCitationTag | null {
  const legacyMatch = /^<citation>([^<]*)<\/citation>$/.exec(tag)
  if (legacyMatch) {
    const trimmed = (legacyMatch[1] ?? '').trim()
    return trimmed ? { postId: trimmed, postTextId: null } : null
  }
  const postId = extractCitationAttr(tag, 'post_id')
  return postId
    ? { postId, postTextId: extractCitationAttr(tag, 'post_text_id') }
    : null
}

/**
 * Replace each verbatim blockquote+citation block with a `<artifact
 * type="verbatim" id="vb_N" />` placeholder, collecting the matched record
 * id / post_text id / quote text for each. Reuses the existing artifact
 * placeholder + split mechanism (see COMBINED_ARTIFACT_REGEX below) rather
 * than inventing a second one — charts/annotations already solved
 * "interleave a rendered block between markdown parts."
 *
 * A block that doesn't cleanly resolve to exactly one valid citation is left
 * completely untouched: a plain non-cited blockquote, a still-streaming
 * `> "quo` with no closing tag yet, two citations spliced into one quote
 * (the system prompt forbids this), or a quote that's empty once the tag and
 * surrounding punctuation are stripped.
 */
function extractVerbatimBlocks(content: string): {
  content: string
  verbatims: VerbatimCitation[]
} {
  const verbatims: VerbatimCitation[] = []

  const newContent = content.replace(
    VERBATIM_BLOCK_REGEX,
    (fullMatch: string, boundary: string, blockBody: string) => {
      const joined = blockBody
        .split('\n')
        .map((line) => line.replace(/^[ \t]*>[ \t]?/, ''))
        .join(' ')
        .trim()

      const matches = [...joined.matchAll(VERBATIM_CITATION_REGEX)]
      if (matches.length !== 1) {
        return fullMatch
      }

      const parsed = parseCitationTag(matches[0]?.[0] ?? '')
      if (!parsed) {
        return fullMatch
      }

      const quote = joined
        .replace(VERBATIM_CITATION_REGEX, '')
        .trim()
        .replace(SURROUNDING_QUOTE_OPEN, '')
        .replace(SURROUNDING_QUOTE_CLOSE, '')
        .trim()
      if (!quote) {
        return fullMatch
      }

      const placeholderId = `vb_${verbatims.length}`
      verbatims.push({
        postId: parsed.postId,
        postTextId: parsed.postTextId,
        quote,
      })
      return `${boundary}<artifact type="verbatim" id="${placeholderId}" />`
    },
  )

  return { content: newContent, verbatims }
}

/**
 * Flatten every verbatim block in raw markdown `content` down to a bare
 * `> "<quote>"` line — metadata line and citation tag dropped — so
 * copy-to-clipboard text never claims metadata the screen didn't render.
 * Full parity with the rendered card (name/source/stars/date) isn't
 * possible here since those come from an API this function doesn't call.
 */
export function flattenVerbatimBlocks(content: string): string {
  return content.replace(
    VERBATIM_BLOCK_REGEX,
    (fullMatch: string, boundary: string, blockBody: string) => {
      const joined = blockBody
        .split('\n')
        .map((line) => line.replace(/^[ \t]*>[ \t]?/, ''))
        .join(' ')
        .trim()

      const matches = [...joined.matchAll(VERBATIM_CITATION_REGEX)]
      if (matches.length !== 1 || !parseCitationTag(matches[0]?.[0] ?? '')) {
        return fullMatch
      }

      const quote = joined
        .replace(VERBATIM_CITATION_REGEX, '')
        .trim()
        .replace(SURROUNDING_QUOTE_OPEN, '')
        .replace(SURROUNDING_QUOTE_CLOSE, '')
        .trim()
      if (!quote) {
        return fullMatch
      }

      return `${boundary}> "${quote}"`
    },
  )
}

export function processMessageContent(content: string): ProcessedParts {
  const citations: Record<string, number> = {}
  let citationIndex = 1
  const tables: Array<{ resultIds: string[]; title?: string }> = []
  const tableMarkdown: string[] = []

  // Record the raw markdown source for each CLOSED `<table>…</table>` block —
  // aligned with tables[] by source order. Streaming partial tables (opening
  // tag with no `</table>` yet) still get their tables[] entry from the
  // opening-tag pass below, but no tableMarkdown[] entry until they close,
  // so TableToolbar only renders once the full block has arrived.
  TABLE_BLOCK_REGEX.lastIndex = 0
  let blockMatch
  while ((blockMatch = TABLE_BLOCK_REGEX.exec(content)) !== null) {
    tableMarkdown.push((blockMatch[3] ?? '').trim())
  }

  // Extract table metadata and strip the opening tag. Titles render as an
  // AssetCaption inside TableContainer — not promoted into markdown headings.
  let contentWithTables = content.replace(
    TABLE_OPENING_REGEX,
    (_, resultIdsStr: string, title: string) => {
      const resultIds = resultIdsStr
        .split(',')
        .map((id) => id.trim())
        .filter((id) => id.length > 0)
      tables.push({ resultIds, title: title || undefined })
      return ''
    },
  )

  contentWithTables = contentWithTables.replace(TABLE_CLOSING_REGEX, '')

  // Collapse runs of consecutive `---` so the model can't stack dividers.
  contentWithTables = contentWithTables.replace(
    /(^|\n)(?:---\s*\n\s*){2,}---/g,
    '$1---',
  )

  // Replace web-citation tags with data-attribute spans for WebCitationPill.
  // Attributes are parsed in any order so the model's output order doesn't
  // matter; malformed tags simply yield empty attribute values.
  //
  // Dedup: when the LLM cites the same source (URL) multiple times in one
  // message — e.g. five Monday items from the same board, or a Yogi follow-up
  // that re-cites the same Asana task — only the FIRST occurrence renders as
  // a pill. Subsequent tags with the same non-empty URL collapse to empty
  // strings so the prose still reads coherently but the UI shows one chip
  // per source entity. The PM constraint is "cite the container, not each
  // line item." Tags with an empty URL (malformed/unparseable) are never
  // deduped — they always render as a non-navigable pill for visibility.
  const seenCitationUrls = new Set<string>()
  const contentWithWebCitations = contentWithTables.replace(
    WEB_CITATION_REGEX,
    (_, attrs: string) => {
      const parsed: Record<string, string> = {}
      WEB_CITATION_ATTR.lastIndex = 0
      let attrMatch
      while ((attrMatch = WEB_CITATION_ATTR.exec(attrs)) !== null) {
        parsed[attrMatch[1]!] = attrMatch[2]!
      }
      const { url = '', title = '', source = '' } = parsed
      if (url) {
        if (seenCitationUrls.has(url)) {
          return ''
        }
        seenCitationUrls.add(url)
      }
      return `<span data-wc-url="${url}" data-wc-title="${title}" data-wc-source="${source}"></span>`
    },
  )

  // Extract verbatim blockquote+citation blocks into artifact placeholders
  // BEFORE the citation→span rewrite below (so it matches raw <citation>
  // tags, not already-rewritten spans) and before the artifact split (its
  // placeholder IS a split boundary — see processedContent below). Running
  // it here also keeps the inline `[N]` numbering assigned below contiguous:
  // a verbatim card consumes no numbered slot.
  const { content: contentWithVerbatims, verbatims: extractedVerbatims } =
    extractVerbatimBlocks(contentWithWebCitations)

  // Replace citation tags with numbered span markup
  const processedContent = contentWithVerbatims.replace(
    CITATION_REGEX,
    (fullMatch: string) => {
      const parsed = parseCitationTag(fullMatch)
      if (!parsed) {
        return fullMatch
      }
      if (!citations[parsed.postId]) {
        citations[parsed.postId] = citationIndex++
      }
      return `<span data-citation-id="${parsed.postId}" data-citation-index="${citations[parsed.postId]}">[${citations[parsed.postId]}]</span>`
    },
  )

  // Split content at artifact placeholders.
  // Each slot in parts[], chartArtifactIds[], annotationArtifactIds[], and
  // verbatims[] is aligned by index.
  const parts: string[] = []
  const charts: string[] = []
  const chartArtifactIds: (string | null)[] = []
  const annotationArtifactIds: (string | null)[] = []
  const verbatims: (VerbatimCitation | null)[] = []
  let lastIndex = 0
  COMBINED_ARTIFACT_REGEX.lastIndex = 0
  let artifactMatch
  while (
    (artifactMatch = COMBINED_ARTIFACT_REGEX.exec(processedContent)) !== null
  ) {
    parts.push(processedContent.slice(lastIndex, artifactMatch.index))
    const fullMatch = artifactMatch[1]!

    if (fullMatch.startsWith('<artifact')) {
      const typeMatch = ARTIFACT_TYPE_RE.exec(fullMatch)
      const artifactType = typeMatch ? (typeMatch[1] ?? null) : null
      const idMatch = ARTIFACT_ID_RE.exec(fullMatch)
      const artifactId = idMatch ? (idMatch[1] ?? null) : null

      if (artifactType === 'chart') {
        charts.push('')
        chartArtifactIds.push(artifactId)
        annotationArtifactIds.push(null)
        verbatims.push(null)
      } else if (artifactType === 'annotation') {
        charts.push('')
        chartArtifactIds.push(null)
        annotationArtifactIds.push(artifactId)
        verbatims.push(null)
      } else if (artifactType === 'verbatim') {
        const index = artifactId?.startsWith('vb_')
          ? Number(artifactId.slice('vb_'.length))
          : Number.NaN
        charts.push('')
        chartArtifactIds.push(null)
        annotationArtifactIds.push(null)
        verbatims.push(extractedVerbatims[index] ?? null)
      } else {
        // Unknown artifact type — treat as no-op slot to preserve part alignment
        charts.push('')
        chartArtifactIds.push(null)
        annotationArtifactIds.push(null)
        verbatims.push(null)
      }
    } else {
      // Legacy <chart>...</chart> format
      charts.push(fullMatch)
      chartArtifactIds.push(null)
      annotationArtifactIds.push(null)
      verbatims.push(null)
    }
    lastIndex = artifactMatch.index + fullMatch.length
  }
  parts.push(processedContent.slice(lastIndex))

  const verbatimPostIds = extractedVerbatims.map((v) => v.postId)
  const postTextIds = Array.from(
    new Set(
      extractedVerbatims
        .map((v) => v.postTextId)
        .filter((id): id is string => id != null),
    ),
  )

  return {
    parts,
    charts,
    chartArtifactIds,
    annotationArtifactIds,
    verbatims,
    tables,
    tableMarkdown,
    // `citations` maps id -> 1-based index in first-seen order (inline pills
    // only); verbatim record ids are unioned in without consuming an index.
    citationIds: Array.from(
      new Set([...verbatimPostIds, ...Object.keys(citations)]),
    ),
    postTextIds,
  }
}
